import React from 'react';

// Phase pill row + progress bar for discovery / backtest jobs.
// Phases for discovery: feature_load, ic, bh, enet, rf, voting, composite
// Phases for backtest:  signal_load, engine, attribution, stress, dsr, kill_gates

const DISCOVERY_PHASES = ['feature_load', 'ic', 'bh', 'enet', 'rf', 'voting', 'composite'];
const BACKTEST_PHASES  = ['signal_load', 'engine', 'attribution', 'stress', 'dsr', 'kill_gates'];

const PHASE_LABELS = {
  feature_load: 'Features',
  ic:           'Univariate IC',
  bh:           'BH-FDR',
  enet:         'ElasticNet',
  rf:           'Random Forest',
  voting:       '2-of-3 Voting',
  composite:    'Composite',
  signal_load:  'Load Signal',
  engine:       'Backtest',
  attribution:  'Attribution',
  stress:       'Stress',
  dsr:          'DSR',
  kill_gates:   'Kill Gates',
};

const STATUS_COLOR = {
  done:                  'text-emerald-600 dark:text-emerald-400',
  killed:                'text-red-500 dark:text-red-400',
  failed:                'text-red-500 dark:text-red-400',
  cancelled:             'text-gray-500',
  interrupted_resumable: 'text-amber-500 dark:text-amber-400',
  running:               'text-brand-gold',
  queued:                'text-gray-500',
};

const JobStatusBar = ({ job, type = 'discovery' }) => {
  if (!job) return null;
  const phases = type === 'backtest' ? BACKTEST_PHASES : DISCOVERY_PHASES;
  const completed = new Set(job.completed_phases || []);
  const active = job.progress_phase;
  const pct = Math.max(0, Math.min(100, job.progress_pct || 0));
  const statusColor = STATUS_COLOR[job.status] || 'text-gray-500';

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className={`text-xs uppercase tracking-wider font-semibold ${statusColor}`}>
            {job.status}
          </span>
          {job.progress_message && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
              {job.progress_message}
            </span>
          )}
        </div>
        <span className="text-sm font-mono text-gray-600 dark:text-gray-400">{pct}%</span>
      </div>

      <div className="w-full h-1 bg-gray-100 dark:bg-gray-800 rounded mb-4">
        <div
          className="h-1 bg-brand-gold rounded transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {phases.map(phase => {
          const isDone = completed.has(phase);
          const isActive = active === phase && !isDone;
          let cls;
          if (isActive) {
            cls = 'bg-brand-gold/15 text-brand-gold border-brand-gold/30';
          } else if (isDone) {
            cls = 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700/40';
          } else {
            cls = 'bg-gray-50 dark:bg-gray-800/50 text-gray-400 border-gray-200 dark:border-gray-700';
          }
          return (
            <span
              key={phase}
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}
            >
              {PHASE_LABELS[phase] || phase}
              {isDone && ' ✓'}
            </span>
          );
        })}
      </div>

      {job.error_message && (
        <div className="mt-3 text-xs text-red-500 dark:text-red-400 font-mono">
          {job.error_message}
        </div>
      )}
      {job.kill_gate_failed && (
        <div className="mt-3 text-xs text-red-500 dark:text-red-400">
          Kill gate failed: <span className="font-mono">{job.kill_gate_failed}</span>
        </div>
      )}
    </div>
  );
};

export default JobStatusBar;
